import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { GetOfferDto, GetOffersBySoQuery, GetOffersBySoQueryVariables } from '../types/types';

/**
 * All offers for a sales order (checkout flow).
 *
 * @param soId - Encrypted SO id (same as getSo / route param)
 */
export const getOffersBySo = gql`
  query GetOffersBySo($soId: String!) {
    getOffersBySo(soId: $soId) {
      offers {
        _id
        offerNumber
        status
        soId
        productTypeId
        departmentCode

        productTypeSpecific {
          isPublished

          building {
            package {
              productId
              code
              label
              description
              propertyType
            }
            coverages {
              code
              label
              description
            }
            quote
          }

          household {
            package {
              productId
              code
              label
              description
              propertyType
            }
            coverages {
              code
              label
              description
            }
            quote
          }

          payment {
            basePremium
            discount
            totalPremium
            singlePremium
            installmentsPremium
            paymentMethod
          }
          newPolicy {
            newPolicyStartDate
            policyDuration
            newPolicyEndDate
            cancelContract
          }
          calculationData {
            calculationId
            packageCode
            packageName
            contractId
            sessionId
            insuredInterestId
            contractorId
            insuredPersonId
            nonStandardRequestToggle
            isFlood
            commercialDiscount
            coverages {
              code
              name
              description
              premium
              insuredAmount
              risks {
                code
                name
                description
                premium
                insuredAmount
                claims {
                  name
                  premium
                  insuredAmount
                }
              }
            }
            additionalCoverages {
              code
              name
              description
              premium
              insuredAmount
              risks {
                code
                name
                description
                premium
                insuredAmount
                claims {
                  name
                  premium
                  insuredAmount
                }
              }
            }
          }
          lastPaymentAttempt
          paidAt
          policyLengthType
          numberOfInstallments
          brandId {
            code
            name
            logos {
              dzo {
                details
              }
            }
          }
          shoppingCartId
          attachedDocuments
          termsAndConditions
        }

        createdAt
        updatedAt
        deleted
        archived
      }
    }
  }
`;

export function useGetOffersBySo(soId: string | undefined) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const { data, loading, error, refetch } = useQuery<GetOffersBySoQuery, GetOffersBySoQueryVariables>(
    getOffersBySo,
    {
      variables: { soId: soId! },
      skip: !soId,
    },
  );

  const isLoading = !isInitialized || loading;

  return {
    offers: data?.getOffersBySo?.offers as GetOfferDto[] ?? [],
    loading: isLoading,
    error,
    refetch,
  };
}
